import { Component, computed, input } from '@angular/core';
import { LaserTreatmentControlDisplay } from './laser-treatment-control-display';
import { LaserTreatmentItem } from './laser-treatment-item';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';

/**
 * Print-only view for the Laser widget used in generated documents.
 */
@Component({
  selector: 'fb-laser-widget-print',
  imports: [LaserTreatmentControlDisplay],
  templateUrl: './laser-widget-print.html',
  styleUrl: './laser-widget-print.scss',
})
export class LaserWidgetPrint {
  /**
   * Laser data to be displayed.
   */
  public readonly data = input<readonly LaserTreatmentItem[]>([]);

  /**
   * Whether the entity has `completed` status.
   */
  public readonly isCompleted = input.required();
  public readonly isTemplatePrint = input(false);

  /**
   * Records to be displayed.
   */
  public readonly records = computed(() => {
    return this.data().map((treatment) => ({
      item: {
        date: treatment.date,
        equipment: this.getDisplayText(treatment.equipmentName),
        procedureType: this.getDisplayText(treatment.procedureTypeName),
        smartCool: treatment.smartCool,
        joules: treatment.joules,
        repetitionRate: treatment.repetitionRate,
        serviceTime: treatment.serviceTime,
        pulse: treatment.pulse,
        note: treatment.note,
        firstLaserOption: this.getDisplayText(treatment.firstLaserOptionName),
        secondLaserOption: this.getDisplayText(treatment.secondLaserOptionName),
      },
      labels: {
        smartCoolText: treatment.labels.smartCoolText,
        joulesText: treatment.labels.joulesText,
        pulseText: treatment.labels.pulseText,
        repetitionRateText: treatment.labels.repetitionRateText,
        firstLaserOptionText: treatment.labels.firstLaserOptionText,
        secondLaserOptionText: treatment.labels.secondLaserOptionText,
      },
      markerTarget: treatment as unknown as ScribeSuggestionMarkerTarget,
    }));
  });

  public readonly visibleRecords = computed(() => this.getVisibleRecords());

  private getDisplayText(text: string | null | undefined): string | null {
    if (typeof text === 'string' && text.trim() !== '') {
      return text.trim();
    }

    return null;
  }

  private isRecordMeaningful(record: {
    date: Date | null;
    equipment: string | null;
    procedureType: string | null;
    smartCool: number | null;
    joules: number | null;
    repetitionRate: number | null;
    serviceTime: number | null;
    pulse: number | null;
    note: string | null;
    firstLaserOption: string | null;
    secondLaserOption: string | null;
  }): boolean {
    if (record.smartCool !== null || record.joules !== null || record.repetitionRate !== null) {
      return true;
    }

    if (record.serviceTime !== null || record.pulse !== null) {
      return true;
    }

    if (
      record.equipment ||
      record.procedureType ||
      record.firstLaserOption ||
      record.secondLaserOption
    ) {
      return true;
    }

    return typeof record.note === 'string' && record.note.trim() !== '';
  }

  private getVisibleRecords() {
    if (this.isTemplatePrint()) {
      return this.records();
    }

    return this.records().filter((record) => this.isRecordMeaningful(record.item));
  }
}
